import React, { useState } from "react";
import PropTypes from "prop-types";

import Text from "components/common/Text";
import Select from "components/common/Select";
import RadioButton from "components/common/RadioButtons";
import { hasPermission } from "hooks/usePermission";
import EligibilityField from "components/Screens/Setting/DynamicForm/EligibilityField";
import EncashmentRules from "components/Screens/Setting/DynamicForm/EncashmentRules";
import CarryForwardRules from "components/Screens/Setting/DynamicForm/CarryForwardRules";
import AccrualRules from "components/Screens/Setting/DynamicForm/AccrualRules";

import "./DynamicForm.scss";

function DynamicForm({ sections, onSubmit, onCancel, initialValues = {}, showActionButtons = true }) {
  const [formValues, setFormValues] = useState(initialValues);

  const handleChange = (name, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formValues);
  };

  const shouldShowField = (field) => {
    if (field.showIf && typeof field.showIf === "function") {
      return field.showIf(formValues);
    }
    return true;
  };

  const renderField = (field) => {
    const { type, name, label, options, customComponent, isDisabled = false, ...rest } = field;
    const value = formValues[name] || "";
    const { canEncash = "No", canCarryForward = "No", accrueOverTime = "No" } = formValues;

    if (customComponent === "eligibility") {
      const eligibilityFormValues = formValues[name] || {};

      return (
        <EligibilityField
          field={field}
          value={eligibilityFormValues}
          name={name}
          setFormValues={setFormValues}
          isDisabled={isDisabled}
        />
      );
    }

    if (customComponent === "accrualRules" && accrueOverTime === "yes") {
      const accrualFormValues = formValues[name] || {};

      return (
        <div>
          {label && <div className="dynamic-form-label-class">{label}</div>}
          <AccrualRules
            field={field}
            value={accrualFormValues}
            name={name}
            setFormValues={setFormValues}
            isDisabled={isDisabled}
          />
        </div>
      );
    }

    if (customComponent === "encashment" && canEncash === "yes") {
      const encashmentFormValues = formValues[name] || {};

      return (
        <EncashmentRules
          field={field}
          value={encashmentFormValues}
          name={name}
          setFormValues={setFormValues}
          isDisabled={isDisabled}
        />
      );
    }

    if (customComponent === "carryForward" && canCarryForward === "yes") {
      const carryForwardFormValues = formValues[name] || {};

      return (
        <CarryForwardRules
          field={field}
          value={carryForwardFormValues}
          name={name}
          setFormValues={setFormValues}
          isDisabled={isDisabled}
        />
      );
    }

    switch (type) {
      case "text":
      case "number":
        return (
          <Text
            name={name}
            label={label}
            type={type}
            value={value}
            onChange={(e) => handleChange(name, e.target.value)}
            labelClass="dynamic-form-label-class"
            disabled={isDisabled}
            {...rest}
          />
        );
      case "select":
        return (
          <Select
            name={name}
            label={label}
            options={options}
            value={value}
            onChange={(newValue) => handleChange(name, newValue)}
            labelClass="dynamic-form-label-class"
            disabled={isDisabled}
            {...rest}
          />
        );
      case "radio":
        return (
          <div className="radio-group-container">
            <label
              htmlFor={name}
              className="radio-group-label para-body-m-medium dynamic-form-label-class para-body-m-medium"
            >
              {label}
            </label>
            <div className="radio-options">
              {options.map((option) => (
                <RadioButton
                  key={option.value}
                  name={name}
                  label={option.label}
                  value={option.value}
                  checked={value === option.value}
                  onChange={handleRadioChange}
                  disabled={isDisabled}
                  labelClass="para-body-m-medium"
                  {...rest}
                />
              ))}
            </div>
          </div>
        );
      case "input-with-suffix":
        return (
          <div className="input-with-suffix-container">
            <Text
              name={name}
              label={label}
              type="number"
              value={value}
              onChange={(e) => handleChange(name, e.target.value)}
              labelClass="dynamic-form-label-class"
              slotProps={{
                htmlInput: { style: { paddingRight: "70px" } },
              }}
              sx={{ width: "100%" }}
              disabled={isDisabled}
              {...rest}
            />
            <span className="input-suffix">{field.suffix}</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <form className="dynamic-form" onSubmit={handleSubmit}>
      {sections.map((section, index) => (
        <>
          <section key={section.title || `section-${section.fields[0]?.name}`} className="form-section">
            {section.title && <div className="heading-h3-semibold color-text-heading-primary">{section.title}</div>}
            {section.description && (
              <p className="mb-1 text-headings-secondary para-body-s-regular">{section.description}</p>
            )}
            <div className="form-fields">
              {section.fields.map(
                (field) =>
                  shouldShowField(field) && (
                    <div key={field.name} className="form-field">
                      {renderField(field)}
                    </div>
                  )
              )}
            </div>
          </section>
          {index < sections.length - 1 && <div className="form-section-divider" />}
        </>
      ))}

      {showActionButtons && (
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          {hasPermission("timeoff.configure") && (
            <button type="submit" className="submit-button">
              Save Changes
            </button>
          )}
        </div>
      )}
    </form>
  );
}

DynamicForm.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.oneOf(["text", "number", "select", "radio", "input-with-suffix"]),
          customComponent: PropTypes.oneOf(["eligibility", "encashment", "carryForward"]),
          name: PropTypes.string.isRequired,
          label: PropTypes.string,
          isDisabled: PropTypes.bool,
          showIf: PropTypes.func,
          options: PropTypes.arrayOf(
            PropTypes.shape({
              value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
              label: PropTypes.string.isRequired,
            })
          ),
        })
      ).isRequired,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({}),
  showActionButtons: PropTypes.bool,
};

DynamicForm.defaultProps = {
  initialValues: {},
  showActionButtons: true,
};

export default DynamicForm;
